import * as React from "react";
import { useState } from "react";
import { ChatMessage } from "../../types/chatTypes";
import type { ChatStep } from "../../types/stream.types";
import { Source } from "../../../../types";
import { Thought, ToolResultThought, ThoughtItem } from "./thoughtRenderer/index";
import MessageActionBar from "./MessageActionBar";
import { useMessageChanges } from "../../hooks/useMessageChanges";
import { copyToClipboard } from "@/lib/utils";
import { Card } from "@/components/ui/card";
import { Loader2 } from "lucide-react";
import { MarkdownMessage } from "./markdown/MarkdownMessage";
import { ChatSteps } from "../chat-timeline/chat-steps";

interface BotMessageProps {
  message: ChatMessage & { 
    sources?: Source[];
    changes?: string;
    completedThoughts?: any[];
    agentTimelineSteps?: ChatStep[];
    changesStatus?: 'applied' | 'cancelled' | 'accepted' | 'rejected' | null;
    changesUpdatedAt?: Date | null;
    streamedFinalText?: string;
    finalResponseComplete?: boolean;
    finalResponseTraceId?: string | null;
    finalChunkCount?: number;
    lastChunkSequence?: number | null;
  };
  onFeedback: (messageId: string, feedback: 'like' | 'dislike') => void;
  onUpdateChangesStatus?: (messageId: string, status: 'applied' | 'cancelled' | 'accepted' | 'rejected') => Promise<void>;
  onViewDocument?: (url: string, name: string) => void;
  onRetry?: (messageId: string) => void;
  previousMessage?: ChatMessage | null;
}

const BotMessage: React.FC<BotMessageProps> = ({ 
  message, 
  onFeedback, 
  onUpdateChangesStatus, 
  onViewDocument,
  onRetry,
  previousMessage
}) => {
  const [isCopied, setIsCopied] = useState(false);
  const [changesListOpen, setChangesListOpen] = useState(false);
  
  const hasPreviousUserMessage = previousMessage?.isUser === true;

  const {
    hasOoxmlChanges,
    ooxmlChanges,
    effectiveChangesStatus,
    changesCount,
  } = useMessageChanges({
    id: message.id,
    changes: message.changes,
    changesStatus: message.changesStatus,
    changesUpdatedAt: message.changesUpdatedAt,
    timestamp: message.timestamp
  });

  const displayContent = message.content || '';
  
  const handleCopyMessage = async () => {
    const success = await copyToClipboard(displayContent);
    if (success) {
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    }
  };
  
  const getCompletedThoughts = () => {
    let thoughts: any[] = [];
    
    if (message.completedThoughts && Array.isArray(message.completedThoughts) && message.completedThoughts.length > 0) {
      thoughts = message.completedThoughts.filter(t => t && t.thought && t.thought.trim());
    } else if ((message as any).completed_thoughts) {
      const rawThoughts = (message as any).completed_thoughts;
      if (Array.isArray(rawThoughts) && rawThoughts.length > 0) {
        thoughts = rawThoughts.map((thought: any, index: number) => {
          if (typeof thought === 'string') {
            try {
              const parsed = JSON.parse(thought);
              return {
                agent: parsed.agent || 'WorkOps Agent',
                thought: parsed.thought || parsed.content || thought,
                completion_order: parsed.completion_order || index
              };
            } catch {
              return {
                agent: 'WorkOps Agent',
                thought: thought,
                completion_order: index
              };
            }
          } else if (thought && typeof thought === 'object' && thought.thought) {
            return {
              agent: thought.agent || 'WorkOps Agent',
              thought: thought.thought || thought.content || '',
              completion_order: thought.completion_order || index
            };
          }
          return null;
        }).filter(Boolean);
      }
    }
    
    return thoughts;
  };
  
  const completedThoughts = getCompletedThoughts();
  const hasThoughts = completedThoughts.length > 0;
  const isFinalResponseComplete = message.finalResponseComplete !== false;
  const areThoughtsCompleted = message.id !== 'agent-streaming' || isFinalResponseComplete;
  const isFinalResponseStreaming = message.id === 'agent-streaming' && !isFinalResponseComplete;

  const timelineSteps = message.agentTimelineSteps;
  const isStreamingUi = message.id === 'agent-streaming' && !isFinalResponseComplete;
  const showChatTimeline =
    isStreamingUi || (Array.isArray(timelineSteps) && timelineSteps.length > 0);
  const hasTextOutputInTimeline = Boolean(
    timelineSteps?.some((s) => s.type === 'text_output')
  );

  const normalizeThought = (thought: any): Thought => {
    if (thought.toolResult || thought.type === 'tool_result' || thought.tool_name) {
      const tr = thought.toolResult || thought;
      return {
        type: 'tool_result',
        agent: thought.agent || tr.agent || 'WorkOps Agent',
        tool_name: tr.tool_name || thought.tool_name || 'unknown',
        tool_call_id: tr.tool_call_id || thought.tool_call_id,
        success: tr.success !== undefined ? tr.success : true,
        content: tr.content || thought.content || thought.thought || '',
        thought: thought.thought || tr.content || `Tool ${tr.tool_name || 'unknown'} executed`,
        completion_order: thought.completion_order || 0,
        metadata: tr.metadata || thought.metadata || {},
        toolResult: tr.toolResult || (tr.tool_name ? tr : undefined),
        timestamp: tr.timestamp || thought.timestamp
      } as ToolResultThought;
    }
    
    return {
      type: 'agent_update',
      agent: thought.agent || 'WorkOps Agent',
      thought: thought.thought || thought.content || thought.message || '',
      is_final: thought.is_final !== undefined ? thought.is_final : true,
      completion_order: thought.completion_order || 0
    } as Thought;
  };

  const actionBar = areThoughtsCompleted ? (
    <MessageActionBar
      key={`action-bar-${message.id}-${effectiveChangesStatus}`}
      messageId={message.id}
      currentFeedback={message.feedbackGiven}
      onFeedback={onFeedback}
      onCopy={handleCopyMessage}
      isCopied={isCopied}
      showRetry={hasPreviousUserMessage && Boolean(onRetry)}
      onRetry={onRetry ? () => onRetry(message.id) : undefined}
      hasChanges={hasOoxmlChanges}
      ooxmlChanges={ooxmlChanges}
      changesCount={changesCount}
      changesStatus={effectiveChangesStatus}
      onUpdateChangesStatus={onUpdateChangesStatus}
      onToggleChanges={() => setChangesListOpen(!changesListOpen)}
      isChangesOpen={changesListOpen}
    />
  ) : null;

  return (
    <div className="w-full mb-5 pl-0 group">
      {showChatTimeline && (
        <div className="w-full space-y-3">
          <ChatSteps
            steps={timelineSteps || []}
            isStreaming={isStreamingUi}
            onViewDocument={onViewDocument}
          />
        </div>
      )}

      {!showChatTimeline && hasThoughts && (
        <div className="mb-2 space-y-1 max-w-full min-w-0">
          {completedThoughts.map((thought: any, index: number) => {
            const normalizedThought = normalizeThought(thought);
            const isLast = index === completedThoughts.length - 1;
            
            return (
              <ThoughtItem
                key={`${thought.completion_order || index}-${thought.thought?.substring(0, 20) || thought.toolResult?.tool_name || 'thought'}`}
                thought={normalizedThought}
                isLast={isLast}
                onDocumentClick={(_documentId, url, name) => {
                  if (url && onViewDocument) {
                    onViewDocument(url, name || 'Document');
                  } else if (url) {
                    window.open(url, '_blank');
                  }
                }}
              />
            );
          })}
        </div>
      )}

      {!showChatTimeline && !areThoughtsCompleted && (
        <div className="mb-2 flex items-center gap-2">
          <Loader2 className="h-3 w-3 text-blue-500 animate-spin" />
          <span className="text-[11px] text-muted-foreground italic font-semibold">
            {isFinalResponseStreaming && (message.finalChunkCount || 0) > 0 ? 'Assistant is writing...' : 'Reasoning...'}
          </span>
        </div>
      )}

      {displayContent.trim() && !hasTextOutputInTimeline && (
        <div className="max-w-full min-w-0">
          <Card className="border-none shadow-none bg-white/50 hover:bg-white/80 hover:shadow-none hover:outline-none transition-colors w-full">
            <div className="p-3 text-[14px] text-slate-800 leading-relaxed break-words overflow-hidden w-full">
              <MarkdownMessage 
                content={displayContent}
                fontSize="text-[14px] md:text-[15px]"
                textColor="text-slate-800"
                onViewDocument={onViewDocument}
              />
              {actionBar}
            </div>
          </Card>
        </div>
      )}

      {hasTextOutputInTimeline && areThoughtsCompleted && (
        <div className="flex flex-col gap-3 pt-2 max-w-full min-w-0">
          {actionBar}
        </div>
      )}

    </div>
  );
};

export default BotMessage;
